import React, { useState } from 'react';
import { useAgent } from '../../context/AgentContext';
import {
  FileText,
  File,
  Trash2,
  RefreshCw,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader
} from 'lucide-react';
import './DocumentList.css';

const statusConfig = {
  pending: { icon: Clock, label: 'Pending', color: '#F59E0B' },
  processing: { icon: Loader, label: 'Processing', color: '#3B82F6' },
  ready: { icon: CheckCircle, label: 'Ready', color: '#10B981' },
  error: { icon: AlertCircle, label: 'Error', color: '#EF4444' }
};

const DocumentList = () => {
  const { documents, deleteDocument, loading } = useAgent();
  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      setDeletingId(id);
      try {
        await deleteDocument(id);
      } finally {
        setDeletingId(null);
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (ext === 'pdf') return FileText;
    return File;
  };

  if (loading.documents) {
    return (
      <div className="documents-loading">
        <div className="spinner" />
        <span>Loading documents...</span>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="documents-empty">
        <FileText size={48} className="empty-icon" />
        <h3>No documents yet</h3>
        <p>Upload documents to start building your agent's knowledge base.</p>
      </div>
    );
  }

  return (
    <div className="documents-list">
      <div className="documents-header">
        <span className="documents-count">{documents.length} document(s)</span>
      </div>

      <div className="documents-grid">
        {documents.map((doc) => {
          const FileIcon = getFileIcon(doc.originalName);
          const status = statusConfig[doc.status] || statusConfig.pending;
          const StatusIcon = status.icon;

          return (
            <div key={doc._id} className="document-card">
              <div className="document-icon">
                <FileIcon size={24} />
              </div>

              <div className="document-info">
                <h4 className="document-name" title={doc.originalName}>
                  {doc.originalName}
                </h4>
                <div className="document-meta">
                  <span>{formatFileSize(doc.size)}</span>
                  <span>•</span>
                  <span>{doc.chunkCount || 0} chunks</span>
                </div>
                <span className="document-date">{formatDate(doc.createdAt)}</span>
              </div>

              <div className="document-status" style={{ color: status.color }}>
                <StatusIcon
                  size={16}
                  className={doc.status === 'processing' ? 'spinning' : ''}
                />
                <span>{status.label}</span>
              </div>

              <div className="document-actions">
                <button
                  className="btn btn-icon btn-ghost"
                  onClick={() => handleDelete(doc._id)}
                  disabled={deletingId === doc._id}
                  title="Delete document"
                >
                  {deletingId === doc._id ? (
                    <span className="spinner" style={{ width: 16, height: 16 }} />
                  ) : (
                    <Trash2 size={16} />
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DocumentList;
